import { useState } from 'react';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { useTheme } from '@/react-app/contexts/ThemeContext';
import { toast } from "react-toastify";
import tradeLogo from "../../assets/trade-logo.jpeg";



export default function ForgotPassword() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      // Use the proper API endpoint instead of direct fetch
      const { api } = await import('@/shared/api/endpoints');
      const response = await api.auth.forgotPassword(email);

      if (response.success) {
        setMessage({ type: 'success', text: response.data?.message || 'Password reset email sent successfully!' });
        setIsEmailSent(true);
        toast.success("Password reset email sent successfully!");
      } else {
        setMessage({ type: 'error', text: response.error || 'Failed to send reset email' });
        toast.error(response.error || 'Failed to send reset email');

      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An unexpected error occurred' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'} flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <a href="/" className="flex items-center justify-center space-x-3 mb-8">
            <img src={tradeLogo} alt="logo" className="h-10 w-10 text-blue-600" />

            <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Trade Enrich</span>
          </a>

          {!isEmailSent ? (
            <>
              <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                Forgot Password?
              </h2>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                No worries! Enter your email address and we'll send you a link to reset your password.
              </p>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                Check Your Email
              </h2>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                We've sent a password reset link to <strong>{email}</strong>
              </p>
            </>
          )}
        </div>

        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-2xl p-8`}>
          {message && (
            <div className={`mb-6 p-4 rounded-xl ${message.type === 'success'
              ? 'bg-green-100 border border-green-200 text-green-800 dark:bg-green-900/50 dark:border-green-800 dark:text-green-300'
              : 'bg-red-100 border border-red-200 text-red-800 dark:bg-red-900/50 dark:border-red-800 dark:text-red-300'
              }`}>
              <div className="flex items-center">
                {message.type === 'success' ?
                  <CheckCircle className="h-5 w-5 mr-2" /> :
                  <AlertCircle className="h-5 w-5 mr-2" />
                }
                {message.text}
              </div>
            </div>
          )}

          {!isEmailSent ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Email Address
                </label>
                <div className="relative">
                  <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                  <input
                    type="email"
                    id="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                    placeholder="Enter your registered email"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !email.trim()}
                className="w-full bg-blue-600 text-white px-6 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          ) : (
            <div className="space-y-6">
              <div className={`${isDark ? 'bg-gray-700' : 'bg-blue-50'} rounded-xl p-6`}>
                <h4 className={`font-semibold ${isDark ? 'text-blue-400' : 'text-blue-800'} mb-2`}>
                  What's Next?
                </h4>
                <ul className={`text-sm ${isDark ? 'text-gray-300' : 'text-blue-700'} space-y-2`}>
                  <li>• Check your email inbox (and spam folder)</li>
                  <li>• Click the reset link in the email</li>
                  <li>• Create a new secure password</li>
                  <li>• Login with your new password</li>
                </ul>
              </div>

              <button
                onClick={() => {
                  setIsEmailSent(false);
                  setEmail('');
                  setMessage(null);
                }}
                className="w-full bg-gray-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-gray-700 transition-colors"
              >
                Send to Different Email
              </button>
            </div>
          )}

          <div className="mt-8 text-center">
            <a
              href="/login"
              className={`inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium transition-colors`}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Login</span>
            </a>
          </div>

          <div className="mt-6 text-center space-y-4">
            <div className="flex justify-center space-x-6 text-sm">
              <a href="/register" className={`${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} transition-colors`}>
                Create Account
              </a>
              <a href="/contact" className={`${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} transition-colors`}>
                Need Help?
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
